import HomeView from "@/views/Home.vue";
import { useAuthStore, useAwardStore } from "@/stores";
import { defaultRoutes } from ".";
import type { RouteRecordRaw } from "vue-router";
import { useProductsStore } from "@/stores";

export default [
  {
    path: "/",
    name: "home",
    component: HomeView,
    meta: { requiresAuth: true, authorizedRoles: ["Admin", "Candidate"] },
  },
  {
    path: "/dashboard",
    component: HomeView,
    meta: { requiresAuth: true, authorizedRoles: ["Admin", "Candidate"] },
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/Login.vue"),
    meta: { requiresAuth: false, hideNav: true },
    beforeEnter: () => {
      const authStore = useAuthStore();
      if (authStore.authenticated) {
        return defaultRoutes[authStore.user.role];
      }
    },
  },
  {
    path: "/new-password",
    name: "force-reset-password",
    component: () => import("@/views/ForcePasswordReset.vue"),
    meta: { requiresAuth: false, hideNav: true },
  },
  {
    path: "/signup",
    name: "signup",
    component: () => import("@/views/Signup.vue"),
    meta: { requiresAuth: false, hideNav: true },
  },
  {
    path: "/need-help",
    name: "need-help",
    component: () => import("@/views/ForgotPassword.vue"),
    meta: {
      requiresAuth: false,
      friendlyName: "Forgot Password",
      hideNav: true,
    },
  },
  {
    path: "/approve-user-account/:requestId",
    name: "approve-user-account",
    component: () => import("@/views/ApproveUserAccount.vue"),
    meta: {
      requiresAuth: false,
      friendlyName: "Approve User Account",
      hideNav: true,
    },
  },
  {
    name: "claim-nomination",
    path: "/claim-nomination",
    component: () => import("@/views/CandidateRegistration.vue"),
    meta: {
      requiresAuth: false,
      hideNav: true,
      friendlyName: "Claim Nomination",
    },
  },
  {
    name: "register",
    path: "/register",
    component: () => import("@/views/CandidateRegistration.vue"),
    meta: {
      requiresAuth: false,
      hideNav: true,
      friendlyName: "Candidate Registration",
    },
  },
  {
    path: "/accounting",
    name: "accounting",
    component: () => import("@/views/common/Accounting/Accounting.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin", "Candidate"],
      friendlyName: "Accounting",
    },
  },
  {
    path: "/invoice/:id",
    name: "full-screen-invoice",
    component: () => import("@/views/common/Accounting/Invoice.vue"),
    props: true,
    meta: {
      hideNav: true,
      requiresAuth: true,
      authorizedRoles: ["Admin", "Candidate"],
      friendlyName: "Invoice",
    },
  },
  {
    path: "/graphics",
    name: "graphics",
    component: () => import("@/views/common/Graphics.vue"),
    meta: {
      requiresAuth: true,
      authorizedRoles: ["Admin", "Candidate"],
      friendlyName: "Graphics",
    },
  },
  {
    path: "/activate-account",
    name: "activate-account",
    component: () => import("@/views/SetPassword.vue"),
    meta: {
      hideNav: true,
      requiresAuth: false,
      friendlyName: "Activate",
    },
  },
  {
    path: "/unsubscribe/:email",
    name: "unsubscribe",
    component: () => import("@/views/Unsubscribe.vue"),
    props: true,
    meta: { requiresAuth: false, hideNav: true },
  },
  {
    path: "/buy/:slug",
    name: "buy",
    component: () => import("@/views/common/PurchaseProduct.vue"),
    props: (route) => {
      const productsStore = useProductsStore();
      const awardStore = useAwardStore();
      const product = productsStore.activeProducts.find((p) =>
        p.options.some((o) => o.slug === route.params.slug),
      );
      if (!product) {
        return {};
      }
      const option = product.options.find(
        (o) =>
          o.slug === route.params.slug &&
          (!o.awardYear || o.awardYear === awardStore.awardYear),
      );
      return {
        product,
        option,
      };
    },
    meta: {
      requiresAuth: true,
      hideNav: true,
    },
  },
  {
    path: "/buy-annual-sponsorship",
    name: "buy-annual-sponsorship",
    component: () => import("@/views/common/PurchaseProduct.vue"),
    props: () => {
      const productsStore = useProductsStore();
      return {
        product: productsStore.annualSponsorship,
        returnRoute: {
          name: "sponsorships",
          friendlyName: "Sponsorships",
        },
      };
    },
    meta: {
      requiresAuth: true,
      hideNav: true,
    },
  },
  {
    path: "/buy-voting-stage-sponsorship",
    name: "buy-voting-stage-sponsorship",
    component: () => import("@/views/common/PurchaseProduct.vue"),
    props: () => {
      const productsStore = useProductsStore();
      return {
        product: productsStore.votingStageSponsorship,
        option: productsStore.votingStageSponsorship?.options[0],
        returnRoute: {
          name: "sponsorships",
          friendlyName: "Sponsorships",
        },
      };
    },
    meta: {
      requiresAuth: true,
      hideNav: true,
    },
  },
  {
    path: "/buy-voter-rewards",
    name: "buy-voter-rewards",
    component: () => import("@/views/common/PurchaseProduct.vue"),
    props: () => {
      const productsStore = useProductsStore();
      return {
        product: productsStore.voterRewards,
        option: productsStore.voterRewards?.options[0],
        returnRoute: {
          name: "sponsorships",
          friendlyName: "Sponsorships",
        },
      };
    },
    meta: {
      requiresAuth: true,
      hideNav: true,
    },
  },
  {
    path: "/buy-awards-ceremony-tickets",
    name: "buy-awards-ceremony-tickets",
    component: () => import("@/views/common/PurchaseProduct.vue"),
    props: () => {
      const productsStore = useProductsStore();
      return {
        product: productsStore.ceremonyTicket,
        returnRoute: {
          name: "awards-ceremony",
          friendlyName: "Awards Ceremony",
        },
      };
    },
    meta: {
      requiresAuth: true,
      hideNav: true,
    },
  },
  {
    path: "/buy-plaque",
    name: "buy-plaque",
    component: () => import("@/views/common/PurchaseProduct.vue"),
    props: () => {
      const productsStore = useProductsStore();
      const awardStore = useAwardStore();
      const product = productsStore.plaque;
      const option = productsStore.plaque?.options.find(
        (o) =>
          o.type === "EngravedPlaque" && o.awardYear === awardStore.awardYear,
      );
      if (!product || !option) {
        return {
          product: {},
          option: {},
        };
      }
      return {
        product,
        option,
        returnRoute: {
          name: "plaques",
          friendlyName: "Plaques",
        },
      };
    },
    meta: {
      requiresAuth: true,
      hideNav: true,
    },
  },
  {
    path: "/buy-previous-year-plaque",
    name: "buy-previous-year-plaque",
    component: () => import("@/views/common/PurchaseProduct.vue"),
    props: () => {
      const productsStore = useProductsStore();
      const product = productsStore.plaque;
      const option = productsStore.plaque?.options.find(
        (o) => o.type === "PreviousYearPlaque",
      );
      if (!product || !option) {
        return {
          product: {},
          option: {},
        };
      }
      return {
        product,
        option,
        returnRoute: {
          name: "plaques",
          friendlyName: "Plaques",
        },
      };
    },
    meta: {
      requiresAuth: true,
      hideNav: true,
    },
  },
  {
    path: "/double-deposit",
    name: "double-deposit",
    component: () => import("@/views/common/PurchaseProduct.vue"),
    props: () => {
      const awardStore = useAwardStore();
      const productsStore = useProductsStore();
      return {
        product: productsStore.magazineAdDeposit,
        option: productsStore.magazineAdDeposit?.options.find(
          (o) =>
            o.type === "DoubleDeposit" && o.awardYear === awardStore.awardYear,
        ),
        returnRoute: {
          name: "winners-guide",
          friendlyName: "Winners Guide",
        },
      };
    },
    meta: {
      requiresAuth: true,
      hideNav: true,
    },
  },
  {
    path: "/upgrade-to-vip",
    name: "upgrade-to-vip",
    component: () => import("@/views/common/PurchaseProduct.vue"),
    props: () => {
      const awardStore = useAwardStore();
      const productsStore = useProductsStore();
      const upgrades = productsStore.upgrades;
      if (!upgrades) {
        return;
      }
      const upgradeToVip = upgrades.options.find(
        (o) => o.type === "VIPUpgrade" && o.awardYear === awardStore.awardYear,
      );
      if (!upgradeToVip) {
        return;
      }
      return {
        product: upgrades,
        option: upgradeToVip,
        returnRoute: {
          name: "awards-ceremony",
          friendlyName: "Awards Ceremony",
        },
      };
    },
    meta: {
      requiresAuth: true,
      hideNav: true,
    },
  },
  {
    path: "/sponsor-setup",
    name: "sponsor-setup",
    component: () => import("@/views/common/SponsorSetup/SponsorSetup.vue"),
    meta: {
      requiresAuth: true,
      hideNav: true,
      authorizedRoles: ["Admin", "Candidate"],
      friendlyName: "Sponsor Setup",
    },
  },
] as RouteRecordRaw[];
